/**
 * Reusable modal that runs any of the four xnotify-aiagents.
 *
 * Props:
 *   isOpen       (bool)
 *   onClose      ()
 *   agentId      "predictive-segmentation" | "copy-suggester" | "campaign-performance-analyst" | "compliance-reviewer"
 *   title        string (modal header)
 *   icon         react node (in header)
 *   fields       object — agent-specific payload passed verbatim to the gateway
 *   onResult     (result) — optional callback when the agent returns a result
 *   renderResult (result) => JSX — required, renders the agent-specific result panel
 *   autoRun      bool — if true, kicks off the run on open (otherwise show a "Run" button)
 *   ctaLabel     string — label for the run button (default "Run Agent")
 *   description  string — short blurb shown above the run button
 *
 * Local-only. Do NOT commit.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  Box, Button, Flex, HStack, Spinner, Text, Alert, AlertIcon, Tag, TagLabel,
  Code
} from "@chakra-ui/react";
import { runAgent, health, AGENTS_CONSOLE_URL } from "./aiAgentsClient";

export default function AIAgentModal({
  isOpen, onClose,
  agentId, title, icon,
  fields, onResult, renderResult,
  autoRun = true,
  ctaLabel = "Run Agent",
  description,
  formContent      // optional — JSX rendered in body when status=idle (form fields)
}) {
  const [status, setStatus] = useState("idle"); // idle | health-checking | running | done | error
  const [healthInfo, setHealthInfo] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [stdout, setStdout] = useState("");

  const checkHealth = useCallback(async () => {
    setStatus("health-checking");
    try {
      const h = await health();
      setHealthInfo(h);
      return h;
    } catch (e) {
      setError(`Agents console not reachable at ${AGENTS_CONSOLE_URL}. Start it with: cd xnotify-aiagents/agents-console && npm start`);
      setStatus("error");
      return null;
    }
  }, []);

  const run = useCallback(async () => {
    setError(null); setResult(null); setStdout("");
    setStatus("running");
    try {
      const out = await runAgent(agentId, fields || {});
      setStdout(out.stdout || "");
      // DA-BUG-0267 — the agents-console gateway forces `result = {}`
      // on failed runs so it can stamp run_id on the audit log. A bare
      // `{}` is NOT a useful result — distinguish "real result" by the
      // presence of `schema_version` (every agent envelope sets it).
      // Otherwise an agent crash (e.g. ReferenceError after drafting)
      // silently downgrades into a "done" state with empty variants and
      // the operator never sees the underlying error.
      const realResult = out.result && Object.keys(out.result).length > 0 && out.result.schema_version
        ? out.result
        : null;
      setResult(realResult);
      if (!out.ok) {
        setStatus("error");
        setError(out.error || (out.stderr ? out.stderr.split("\n").filter(Boolean).slice(-1)[0] : "Agent failed"));
      } else {
        setStatus("done");
      }
      if (onResult && realResult) onResult(realResult);
    } catch (e) {
      setError(e.message);
      setStatus("error");
    }
  }, [agentId, fields, onResult]);

  // On open: health check, then (optionally) auto-run
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      const h = await checkHealth();
      if (cancelled || !h) return;
      if (autoRun) await run();
      else setStatus("idle");
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    if (status === "running") return; // don't allow close mid-run
    setStatus("idle"); setError(null); setResult(null); setStdout("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="3xl" scrollBehavior="inside" closeOnOverlayClick={status !== "running"}>
      <ModalOverlay />
      <ModalContent borderRadius="xl">
        <ModalHeader>
          <HStack spacing={3}>
            {icon}
            <Box>
              <Text fontSize="lg" fontWeight="600">{title}</Text>
              {description && <Text fontSize="xs" color="gray.500" mt={0.5}>{description}</Text>}
            </Box>
            <HStack spacing={1} ml="auto" pr={6}>
              {status === "health-checking" && <Tag size="sm"><TagLabel>checking…</TagLabel></Tag>}
              {status === "running"         && <Tag size="sm" colorScheme="blue"><TagLabel>running</TagLabel></Tag>}
              {status === "done"            && <Tag size="sm" colorScheme="green"><TagLabel>done</TagLabel></Tag>}
              {status === "error"           && <Tag size="sm" colorScheme="red"><TagLabel>error</TagLabel></Tag>}
            </HStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={status === "running"} />
        <ModalBody pb={6}>
          {error && (
            <Alert status="error" borderRadius="md" mb={4}>
              <AlertIcon />
              <Box>
                <Text fontWeight="600">{error}</Text>
                {stdout && <Code mt={2} display="block" p={2} fontSize="xs" whiteSpace="pre-wrap">{stdout.slice(-2000)}</Code>}
              </Box>
            </Alert>
          )}
          {status === "health-checking" && (
            <Flex direction="column" align="center" py={8} gap={2}>
              <Spinner size="md" color="blue.500" />
              <Text fontSize="sm" color="gray.500">Connecting to Agents Console at <Code>{AGENTS_CONSOLE_URL}</Code>…</Text>
            </Flex>
          )}
          {status === "running" && (
            <Flex direction="column" align="center" py={10} gap={3}>
              <Spinner size="lg" color="blue.500" thickness="3px" />
              <Text fontSize="sm" color="gray.700" fontWeight="500">Running {title}…</Text>
              <Text fontSize="xs" color="gray.500">Discovering schema → calling MCP → grounding LLM</Text>
            </Flex>
          )}
          {status === "idle" && !autoRun && (
            <Box>
              {formContent && <Box mb={4}>{formContent}</Box>}
              <Flex justify="flex-end">
                <Button colorScheme="blue" onClick={run} size="md" leftIcon={<span>✨</span>}>
                  {ctaLabel}
                </Button>
              </Flex>
            </Box>
          )}
          {status === "done" && result && (
            <Box mt={1}>
              {renderResult ? renderResult(result, { stdout, healthInfo, rerun: run }) : (
                <Code display="block" whiteSpace="pre-wrap" fontSize="xs" p={3} borderRadius="md" bg="gray.50">
                  {JSON.stringify(result, null, 2)}
                </Code>
              )}
              <Flex mt={4} justify="flex-end">
                <Button variant="ghost" size="sm" onClick={run}>↻ Re-run</Button>
                <Button size="sm" colorScheme="blue" onClick={handleClose} ml={2}>Done</Button>
              </Flex>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
